import { objectIdSchema, partyRoleSchema, phoneSchema } from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

/**
 * Account operations — reshape an account after signup without
 * deleting it: change its type, its company roles, merge two orgs, or
 * mark a company member as also driving. One service
 * (`AccountOpsService`) serves both the admin panel and the WhatsApp
 * agent; every operation has a preview (counts, blockers, warnings) and
 * an execute that is safe to re-run.
 *
 * See `apps/backend/src/app/account-ops/README.md`.
 */

export const accountCompanyRoleSchema = partyRoleSchema
  .extract(['carrier_company', 'broker', 'business_customer'])
  .describe(
    'A company role: `carrier_company` runs trucks, `broker` arranges them, `business_customer` ships goods.',
  );
export type AccountCompanyRole = z.infer<typeof accountCompanyRoleSchema>;

export const accountOperationSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('change_org_roles'),
    orgId: objectIdSchema.describe('The org whose company roles change.'),
    roles: z
      .array(accountCompanyRoleSchema)
      .min(1)
      .describe(
        'The full new role list (replaces, not adds). Carrier ↔ broker is a role change; no data moves.',
      ),
  }),
  z.object({
    op: z.literal('to_org_account'),
    userId: objectIdSchema.describe('A freelancer driver or individual who now runs a company.'),
    roles: z.array(accountCompanyRoleSchema).min(1),
    org: z
      .union([
        z.object({ create: z.object({ name: z.string().trim().min(1).max(120) }) }),
        z.object({ existingOrgId: objectIdSchema }),
      ])
      .describe('A new org by name, or (staff only) an existing org to join as admin.'),
    asDriver: z
      .boolean()
      .optional()
      .describe(
        'Keep driving as an owner-operator of the new org. Defaults to true for a carrier.',
      ),
    driverTo: z
      .object({
        phone: phoneSchema,
        name: z.string().trim().min(1).max(120).optional(),
      })
      .optional()
      .describe(
        'With asDriver false: hand the existing driver (his ID, licence, truck and filed waybills) to this phone as a driver member of the new org. Unset: the driver is retired and his truck freed for another driver.',
      ),
  }),
  z.object({
    op: z.literal('to_driver_account'),
    userId: objectIdSchema.describe(
      'A company member who is really a freelancer driver. The sole member converts the whole company into his account; with other people on it he leaves, taking only his driver row and the trucks he drives (staff only).',
    ),
    driverId: objectIdSchema
      .optional()
      .describe(
        "An existing driver row on the company that is really him, under another phone. It becomes his: his login, his phone, his trucks; that row and its own driver-only login are not counted as other people. Staff only; also repairs someone who already left and was given an empty driver row.",
      ),
  }),
  z.object({
    op: z.literal('merge_orgs'),
    sourceOrgId: objectIdSchema.describe(
      'The duplicate org; it is retired and points at the survivor.',
    ),
    survivorOrgId: objectIdSchema.describe('The org that keeps everything.'),
  }),
  z.object({
    op: z.literal('set_member_driver'),
    userId: objectIdSchema,
    orgId: objectIdSchema,
    enabled: z
      .boolean()
      .describe(
        'true: the admin / dispatcher also drives (gets a Driver row); false: the row is set inactive.',
      ),
  }),
]);
export type AccountOperation = z.infer<typeof accountOperationSchema>;
export type AccountOperationKind = AccountOperation['op'];

export const accountOpsRequestSchema = z.object({ operation: accountOperationSchema });

export type AccountOpsIssueCode =
  | 'same_party'
  | 'traded_with_each_other'
  | 'already_merged'
  | 'org_blocked'
  | 'org_has_other_members'
  | 'not_a_member'
  | 'already_in_org'
  | 'already_this_type'
  | 'not_fresh'
  | 'staff_only'
  | 'not_found'
  | 'subscription_canceled_on_source'
  | 'source_has_paid_subscription'
  | 'app_left_on_source'
  | 'account_left_on_source'
  | 'wallet_left_on_source'
  | 'members_signed_out'
  /** `driverTo.phone` is the owner's own number — tick "Also drives" instead. */
  | 'driver_phone_is_owner'
  /**
   * `driverTo.phone` already drives for the org being joined; or, adopting
   * a driver row, another live freelancer / company row has his phone.
   */
  | 'driver_phone_taken'
  /** The driver moves to `detail` (a phone). */
  | 'driver_handed_over'
  /** The driver is retired; `detail` trucks lose him and wait for a new driver. */
  | 'driver_retired'
  /** The wallet goes with the account; `detail` is its balance. */
  | 'wallet_moved'
  /**
   * He leaves a company that keeps its other people (`detail`). Only his
   * driver row and the trucks he drives go with him; the company's
   * orders, وثائق, subscription, Bayan connection and wallet stay.
   */
  | 'left_company'
  /** `detail` trucks he drives leave the company with him. */
  | 'trucks_leave_with_him'
  /** He was the company's only admin; nobody left can manage it. */
  | 'org_left_without_admin'
  /** The picked driver row becomes his; `detail` is the phone it had. */
  | 'driver_adopted'
  /** The picked driver row belongs to another member of the company (not a driver-only login). */
  | 'driver_has_other_user'
  /**
   * His login already has an active subscription of the same product as
   * the company's, so moving the company's would cancel one of them.
   * Staff sort the credits out first.
   */
  | 'subscription_on_both';

export interface AccountOpsIssue {
  code: AccountOpsIssueCode;
  detail?: string;
}

export interface AccountOpsCount {
  collection: string;
  field: string;
  matched: number;
  modified?: number;
}

export interface AccountOpsPreview {
  op: AccountOperationKind;
  /** Execute refuses while this is non-empty. */
  blockers: AccountOpsIssue[];
  /** Said before execute; never stop it. */
  warnings: AccountOpsIssue[];
  /** Documents that will be rewritten, per field. */
  counts: AccountOpsCount[];
  /** Users who will be signed out of web / mobile. */
  affectedUserIds: string[];
}

export interface AccountOpsResult extends AccountOpsPreview {
  done: boolean;
  /** Repeated execute of a finished operation does nothing and says so. */
  alreadyDone?: boolean;
  orgId?: string;
  driverId?: string;
  revokedSessions: number;
  expiredCards: number;
}

export type AccountOpsPreviewAPI = API<z.infer<typeof accountOpsRequestSchema>, AccountOpsPreview>;
export type AccountOpsExecuteAPI = API<z.infer<typeof accountOpsRequestSchema>, AccountOpsResult>;
